import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router';
import { ApiError } from '../../lib/api';
import {
  operatorConsole,
  type PackBuilderLimits,
  type PackBuilderPack,
} from '../../lib/api/operatorConsole';
import { useMe } from '../../auth/MeContext';
import { Pill } from '../../components/ui/Pill';
import { Toast } from '../../components/ui/Toast';
import { useToast } from '../../components/ui/useToast';
import { formatMoney, formatPercent, healthLabel, packStatusLabel } from './packBuilderFormat';
import { DetailsTab } from './tabs/DetailsTab';
import { AssetsTab } from './tabs/AssetsTab';
import { ItemsTab } from './tabs/ItemsTab';
import { OddsTab } from './tabs/OddsTab';
import { PublishTab } from './tabs/PublishTab';

const TABS = [
  { key: 'details', label: 'Details' },
  { key: 'assets', label: 'Assets' },
  { key: 'items', label: 'Items' },
  { key: 'odds', label: 'Odds & Price' },
  { key: 'publish', label: 'Publish' },
] as const;

type PackTabKey = (typeof TABS)[number]['key'];

function isTabKey(value: string | null): value is PackTabKey {
  return TABS.some((t) => t.key === value);
}

export function PackPage() {
  const { id } = useParams<{ id: string }>();
  const packId = Number(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: PackTabKey = isTabKey(tabParam) ? tabParam : 'details';
  const { me } = useMe();
  const currency = me?.operator.currency ?? null;

  const [pack, setPack] = useState<PackBuilderPack | null>(null);
  const [limits, setLimits] = useState<PackBuilderLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const { toast, show, dismiss } = useToast();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        setLoading(true);
        setError(null);
        // Limits (RTP cap, item caps) only travel with the list endpoint.
        const [loaded, list] = await Promise.all([
          operatorConsole.packBuilder.getPack(packId),
          operatorConsole.packBuilder.listPacks(),
        ]);
        if (cancelled) return;
        setPack(loaded);
        setLimits(list.limits);
      } catch (err) {
        if (cancelled) return;
        // 404: feature hidden for this operator, or a pack that is not ours.
        if (err instanceof ApiError && err.status === 404) {
          setHidden(true);
          return;
        }
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [packId]);

  const selectTab = useCallback(
    (next: PackTabKey) => {
      // Clicking the tab that is already in the URL changes nothing.
      if (searchParams.get('tab') === next) return;
      // Switching tabs drops the Items filters on purpose — they belong to
      // that tab and would otherwise leak into every URL of the pack.
      // `replace`: the tab is view state of one page, so Back leaves the pack
      // instead of walking backwards through the tabs.
      setSearchParams(new URLSearchParams({ tab: next }), { replace: true });
    },
    [searchParams, setSearchParams],
  );

  if (hidden) return <Navigate to="/" replace />;
  if (loading) return <div className="p-6 text-[13px] text-[var(--co-muted)]">Loading pack…</div>;
  if (error || !pack || !limits) {
    return (
      <div className="p-6 text-[13px] text-[var(--co-error)]">Error: {error ?? 'Unknown error'}</div>
    );
  }

  const status = packStatusLabel(pack);
  const health = healthLabel(pack.health);
  const tabProps = { pack, limits, currency, onPackChange: setPack, notify: show };
  const activeLabel = TABS.find((t) => t.key === tab)?.label ?? tab;

  return (
    <div className="space-y-4 p-6">
      <div>
        <Link to="/my-packs" className="text-[12px] text-[var(--co-muted)] hover:underline">
          ← My Packs
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold">{pack.title}</h1>
          <span className="font-mono text-[11px] text-[var(--co-muted)]">#{pack.id}</span>
          <Pill tone={status.tone}>{status.label}</Pill>
          <Pill tone={health.tone}>{health.label}</Pill>
        </div>
        <p className="text-[12px] text-[var(--co-muted)]">
          {`${formatMoney(pack.price, pack.currency ?? currency)} · RTP ${formatPercent(pack.stated_rtp)} · ${pack.items_count} cards`}
        </p>
      </div>

      <div role="tablist" aria-label="Pack sections" className="flex gap-1 border-b border-[var(--co-border)]">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => selectTab(t.key)}
            className={[
              '-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
              tab === t.key
                ? 'border-[var(--co-brand)] text-[var(--co-text)]'
                : 'border-transparent text-[var(--co-muted)] hover:text-[var(--co-text)]',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" aria-label={activeLabel}>
        {tab === 'details' && <DetailsTab {...tabProps} />}
        {tab === 'assets' && <AssetsTab {...tabProps} />}
        {tab === 'items' && <ItemsTab {...tabProps} />}
        {tab === 'odds' && <OddsTab {...tabProps} />}
        {tab === 'publish' && <PublishTab {...tabProps} />}
      </div>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  );
}
